/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import { destroy } from 'redux-form';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { validateExchange, resetExchange, fetchExchangeableLines } from '@/actions/exchange';
import { executeRecaptcha } from 'utils/googleRecaptcha';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import { trackEvent } from 'user-analytics';
import { EXCHANGE_BUTTON_CLICKED } from 'constants/TrackEvents';
import FormLayout from './FormLayout';
import { resetPurchase } from '../../../actions/purchase';

/**
 * Component that contains the exchange form to show passing some props to it.
 * @returns Exchange Form component
 */
const ExchangeFirstStep = () => {
  const { t } = useTranslation('exchange');
  const { type: exchangeType } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { lines, linesLoading, linesError } = useSelector((state) => state.exchange);
  const env = useWhitelabelEnvs();
  const { brand } = env;
  const usingRecaptcha = features.EXCHANGE_REQUIRE_RECAPTCHA && env.recaptcha;
  // Off for every tenant that already exchanges through `tenant.exchange_transporter`; only
  // the B2C brand, which has no such default, resolves its line explicitly.
  const lineSelectionEnabled = Boolean(features.EXCHANGE_LINE_SELECTION_ENABLED);
  /**
   * Function to handle the recaptcha token
   */
  const onCaptchaVerify = (onRecaptchaToken) => {
    const action = features.EXCHANGE_RECAPTCHA_ACTION;
    executeRecaptcha(action, (recaptchaToken) => {
      onRecaptchaToken(recaptchaToken);
    });
  };
  useEffect(() => {
    dispatch(resetExchange());
    dispatch(resetPurchase());
    dispatch(destroy('purchaser'));
    // Task 8.1 (openspec/changes/iamsa-b2c-ticket-exchange, platform-sre): fetched after
    // resetExchange so it isn't cleared by it — resetExchange restores the whole slice to
    // initialState. Tenants without the flag never issue this request.
    if (lineSelectionEnabled) dispatch(fetchExchangeableLines());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // It can be 2 types of exchange, itinerary exchange or an open ticket exchange
  const exchangeTitle =
    exchangeType === 'itinerary' ? t('title.modify_itinerary') : t('title.exchange_open_ticket');
  const exchangeTextCTA =
    exchangeType === 'itinerary' ? t('button.modify', { context: brand }) : t('button.exchange');
  const exchangeRedirectText =
    exchangeType === 'itinerary' ? t('button.want_exchange') : t('button.want_modify');
  const addTicketsTitle =
    exchangeType === 'itinerary' ? t('labels.add_ticket') : t('labels.add_ticket_open_ticket');

  const exchangeTypesAllowed = features.EXCHANGE_TYPES;

  if (!exchangeTypesAllowed.includes(exchangeType)) {
    history.replace(`/exchange/${exchangeTypesAllowed[0]}`);
  }

  // The screen this form offers to switch to. There are only two forms — an itinerary change and
  // an open-ticket redemption — so the alternative is always the other one.
  const alternativeExchangeType = exchangeType === 'itinerary' ? 'openTicket' : 'itinerary';
  // Both ends have to be real: a screen that is already redirecting away has no alternative worth
  // offering, and neither has one whose target this tenant does not allow.
  const canOfferAlternative =
    exchangeTypesAllowed.includes(exchangeType) &&
    exchangeTypesAllowed.includes(alternativeExchangeType);

  const generalInitialValues = {
    operationNumber: '',
  };
  const generalValidationScheme = {
    operationNumber: yup
      .string()
      .matches(/^[0-9]+$/, t('validation.only_numbers'))
      .min(8, t('validation.min_digits', { digits: 8 }))
      .max(12, t('validation.max_digits', { digits: 12 }))
      .required(t('validation.operation_number_required')),
  };

  /**
   * Function to handle the submit of the exchange form
   * @param {String} values - Values of the form to be used for the exchange
   */
  const handleSubmit = (values) => {
    const { email, document, operationNumbers, nit, originSlug, destinationSlug, transporter } =
      values;

    /**
     * Function to validate the exchange wrapping the recaptcha token if needed
     * @param {*} [recaptchaToken] - Recaptcha token
     * @returns {function} - Dispatch function
     */
    const validationFunction = (recaptchaToken) =>
      dispatch(
        validateExchange(
          history,
          operationNumbers,
          nit,
          originSlug,
          destinationSlug,
          recaptchaToken,
          email,
          document,
          transporter,
        ),
      );

    if (usingRecaptcha) {
      onCaptchaVerify(validationFunction);
    } else {
      validationFunction();
    }
  };

  const handleRedirectExchangeAlternative = () => {
    history.push(`/exchange/${alternativeExchangeType}`);
  };

  const handleClickButton = () => {
    trackEvent(EXCHANGE_BUTTON_CLICKED);
  };

  return (
    <FormLayout
      title={exchangeTitle}
      ctaText={exchangeTextCTA}
      redirectText={exchangeRedirectText}
      addTicketsTitle={addTicketsTitle}
      handleSubmit={handleSubmit}
      generalInitialValues={generalInitialValues}
      generalValidationScheme={generalValidationScheme}
      handleRedirectExchangeAlternative={handleRedirectExchangeAlternative}
      // Offered only when the screen it leads to is one this tenant allows. Counting the types
      // instead assumed the second one was always `itinerary`: GFA's sandbox carries
      // `['openTicket', 'transfer']`, so the link rendered, pushed `/exchange/itinerary`, and the
      // guard above bounced it straight back to the form it came from.
      showRedirectButton={canOfferAlternative}
      handleClickButton={handleClickButton}
      lineSelectionEnabled={lineSelectionEnabled}
      lines={lines}
      linesLoading={linesLoading}
      linesError={linesError}
    />
  );
};

export default ExchangeFirstStep;
